import { useLayoutEffect, useState } from 'react';

/**
 * The QR code's size, kept within the card's maximum height: text that needs
 * more lines (a long app name in the caption, stacked buttons or help links)
 * takes its room from the QR code instead of scrolling the caption out of
 * view. Grows back to `ideal` when the room returns; never below `min`.
 *
 * Pass `ref` to the QR code's box, which must sit inside EmbeddedCardLayout,
 * which marks the card and its scrolling main area.
 */
export function useQrFitsCard(ideal: number, min: number) {
  const [box, setBox] = useState<HTMLElement | null>(null);
  const [fitted, setFitted] = useState({ ideal, size: ideal });
  const size = fitted.ideal === ideal ? fitted.size : ideal;

  useLayoutEffect(() => {
    const main = box?.closest<HTMLElement>('[data-embedded-main]');
    const card = box?.closest<HTMLElement>('[data-embedded-card]');
    if (!main || !card) return;
    const fit = () => {
      const overflow = main.scrollHeight - main.clientHeight;
      const room = Math.floor(
        parseFloat(getComputedStyle(card).maxHeight) -
          card.getBoundingClientRect().height,
      );
      let next = size;
      if (overflow > 0) next = Math.max(min, size - overflow);
      else if (room > 0) next = Math.min(ideal, size + room);
      if (next !== size) setFitted({ ideal, size: next });
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(card);
    observer.observe(main);
    // At the card's maximum height main stops growing; its content still does.
    for (const child of Array.from(main.children)) observer.observe(child);
    return () => observer.disconnect();
  }, [box, ideal, min, size]);

  return { ref: setBox, size };
}
